import React from "react";
import { useLanguage } from "../../../../context/LanguageContext";
import OrderItemsList from "../../../../components/common/OrderItemList/OrderItemList";
import DiscountSection from "./DiscountSection";
import useCurrency from "../../../../hooks/useCurrency";
import "./OrderSummary.css";

const OrderSummary = ({
  cart,
  validatedItems, // Add validatedItems prop
  summary,
  storeSettings,
  discountAmount,
  appliedPromo,
  promoLoading,
  promoError,
  formData,
  setFormData,
  onApplyDiscount,
  onRemoveDiscount,
  processing,
  isValidating,
  navigate,
  isCalculating = false, // New prop for server-side calculations
}) => {
  const { language } = useLanguage();
  const { formatDisplay } = useCurrency();

  // Translations for order summary
  const translations = {
    en: {
      orderSummary: "Order Summary",
      subtotal: "Subtotal",
      discount: "Discount",
      shipping: "Shipping",
      freeShipping: "Free Shipping",
      tax: "Tax",
      taxIncluded: "included",
      total: "Total",
      placeOrder: "Place Order",
      processingOrder: "Processing Order...",
      validatingCart: "Validating Cart...",
      cartIsEmpty: "Cart is Empty",
      selectShippingMethod: "Select Shipping Method",
      selectPaymentMethod: "Select Payment Method",
      backToCart: "Back to Cart",
      freeShippingQualified: "You qualify for free shipping!",
      freeShippingAlmost: "Spend {amount} more for free shipping",
      calculating: "Calculating...",
      applying: "Applying discount...",
      uploadReceiptFirst: "Upload Receipt First",
    },
    ar: {
      orderSummary: "ملخص الطلب",
      subtotal: "المجموع الفرعي",
      discount: "الخصم",
      shipping: "الشحن",
      freeShipping: "شحن مجاني",
      tax: "الضريبة",
      taxIncluded: "مشمولة",
      total: "المجموع",
      placeOrder: "تأكيد الطلب",
      processingOrder: "جاري معالجة الطلب...",
      validatingCart: "جاري التحقق من السلة...",
      cartIsEmpty: "السلة فارغة",
      selectShippingMethod: "اختر طريقة الشحن",
      selectPaymentMethod: "اختر طريقة الدفع",
      backToCart: "العودة إلى السلة",
      freeShippingQualified: "تستحق الشحن المجاني!",
      freeShippingAlmost: "اشتر بمبلغ {amount} إضافي للحصول على شحن مجاني",
      calculating: "جاري الحساب...",
      applying: "جاري تطبيق الخصم...",
      uploadReceiptFirst: "ارفع الإيصال أولاً",
    },
  };

  const t = translations[language] || translations.en;

  // Debug logging for free shipping data
  // console.log("🚚 OrderSummary received summary:", {
  //   qualifiesForFreeDelivery: summary?.qualifiesForFreeDelivery,
  //   minimumSpendForFreeDelivery: summary?.minimumSpendForFreeDelivery,
  //   subtotal: summary?.subtotal,
  //   shipping: summary?.shipping,
  //   fullSummary: summary,
  // });

  // Use centralized currency formatting
  const formatCurrency = formatDisplay;

  // Format currency for OrderItemsList (just the number)
  const formatCurrencyNumber = (amount) => {
    return parseFloat(amount || 0).toFixed(2);
  };

  const getTaxDisplayText = () => {
    const taxRate = storeSettings?.taxRate || 0;
    const taxIncluded = storeSettings?.taxIncluded || false;

    if (taxRate === 0) {
      return t.tax;
    }

    const percentage = (taxRate * 100).toFixed(1);
    if (taxIncluded) {
      return `${t.tax} (${percentage}% ${t.taxIncluded})`;
    } else {
      return `${t.tax} (${percentage}%)`;
    }
  };

  const getShippingDisplayText = () => {
    // Check if customer qualifies for free shipping
    if (summary.qualifiesForFreeDelivery || summary.shipping === 0) {
      return t.freeShipping;
    }
    return formatCurrency(summary.shipping);
  };

  // Get free shipping notification message
  const getFreeShippingNotification = () => {
    const minimumSpend = summary.minimumSpendForFreeDelivery;
    if (!minimumSpend) return null;

    const subtotal = summary.subtotal || 0;

    if (subtotal >= minimumSpend) {
      return {
        type: "success",
        message: t.freeShippingQualified,
      };
    } else {
      const remaining = minimumSpend - subtotal;
      return {
        type: "info",
        message: t.freeShippingAlmost.replace(
          "{amount}",
          formatCurrency(remaining),
        ),
      };
    }
  };

  // Calculate tax amount based on whether tax is included or not
  const calculateTaxAmount = () => {
    const taxRate = storeSettings?.taxRate || 0;
    const taxIncluded = storeSettings?.taxIncluded || false;

    if (taxRate === 0) return 0;

    if (taxIncluded) {
      // Tax is already included in prices - just show it for transparency
      // Extract tax from the subtotal for display purposes only
      const baseAmount = Math.max(0, summary.subtotal - (discountAmount || 0));
      return (baseAmount * taxRate) / (1 + taxRate);
    } else {
      // Tax is added on top of prices
      const baseAmount = Math.max(0, summary.subtotal - (discountAmount || 0));
      return baseAmount * taxRate;
    }
  };

  // Calculate the final total
  const calculateTotal = () => {
    const taxRate = storeSettings?.taxRate || 0;
    const taxIncluded = storeSettings?.taxIncluded || false;
    const requiresShipping = storeSettings?.requiresShipping !== false;

    let total = summary.subtotal;

    // Apply discount
    total -= discountAmount || 0;

    // Add tax only if not included in prices
    if (taxRate > 0 && !taxIncluded) {
      const taxAmount = calculateTaxAmount();
      total += taxAmount;
    }

    // Add shipping only if required
    if (requiresShipping) {
      total += summary.shipping;
    }

    return Math.max(0, total);
  };

  const isCheckoutDisabled = () => {
    // Check if shipping is required for this store
    const requiresShipping = storeSettings?.requiresShipping !== false;

    return (
      processing ||
      isValidating ||
      isCalculating || // Also disable during calculations
      promoLoading || // Also disable during promo code operations
      isSummaryLoading || // Disable while summary is loading
      !cart?.items?.length ||
      (requiresShipping && !formData.shippingMethod) || // Only require shipping method if shipping is required
      !formData.paymentMethod || // Add payment method validation
      (formData.paymentMethod === "manual_transfer" &&
        !formData.paymentReceipt) || // Require receipt for manual transfer
      calculateTotal() <= 0
    );
  };

  const getCheckoutButtonText = () => {
    // Check if shipping is required for this store
    const requiresShipping = storeSettings?.requiresShipping !== false;

    if (processing) return t.processingOrder;
    if (isValidating) return t.validatingCart;
    if (isCalculating || isSummaryLoading) return t.calculating;
    if (promoLoading) return t.applying;
    if (!cart?.items?.length) return t.cartIsEmpty;
    if (requiresShipping && !formData.shippingMethod)
      return t.selectShippingMethod; // Only check if shipping is required
    if (!formData.paymentMethod) return t.selectPaymentMethod;
    if (
      formData.paymentMethod === "manual_transfer" &&
      !formData.paymentReceipt
    )
      return t.uploadReceiptFirst || "Upload Receipt First";
    return `${t.placeOrder} • ${formatCurrency(calculateTotal())}`;
  };

  const taxAmount = calculateTaxAmount();
  const finalTotal = calculateTotal();

  // Show loading overlay for any server-side calculation
  const showLoadingOverlay = isCalculating || promoLoading;

  // Check if summary data is still loading (initial page load)
  const isSummaryLoading =
    !summary ||
    summary.subtotal === undefined ||
    summary.subtotal === null ||
    // If we have cart items but subtotal is 0, it's likely still calculating
    (cart?.items?.length > 0 &&
      (summary.subtotal === 0 || summary.total === 0));

  // Helper function to render loading placeholder or actual value
  const renderValueOrLoading = (
    value,
    isLoading = isSummaryLoading || showLoadingOverlay,
  ) => {
    if (isLoading) {
      return <span className="loading-placeholder">...</span>;
    }
    return formatCurrency(value);
  };

  return (
    <div
      className={`os-checkout-summary ${
        showLoadingOverlay ? "calculating" : ""
      }`}
    >
      {/* Loading overlay for server-side calculations */}
      {showLoadingOverlay && <div className="os-loading-placeholder]"></div>}

      <h3>{t.orderSummary}</h3>

      <OrderItemsList
        items={validatedItems || cart?.items || []}
        currency={storeSettings?.currency || ""}
        formatCurrency={formatCurrencyNumber}
        variant="checkout"
        showQuantity={true}
        showVariants={true}
        showItemTotal={true}
        showDiscountInfo={false}
        className="order-items-checkout"
      />

      <DiscountSection
        discountCode={formData.discountCode}
        setDiscountCode={(code) =>
          setFormData((prev) => ({ ...prev, discountCode: code }))
        }
        onApplyDiscount={onApplyDiscount}
        onRemoveDiscount={onRemoveDiscount}
        appliedPromo={appliedPromo}
        promoLoading={promoLoading}
        promoError={promoError}
        processing={processing}
        storeSettings={storeSettings}
      />

      <div className="os-order-totals">
        <div className="os-subtotal">
          <span>{t.subtotal}</span>
          {renderValueOrLoading(summary.subtotal)}
        </div>

        {appliedPromo && discountAmount > 0 && (
          <div className="os-discount-line">
            <span>
              {t.discount} ({appliedPromo.code})
            </span>
            <span>-{renderValueOrLoading(discountAmount)}</span>
          </div>
        )}

        {/* Only show shipping if required */}
        {storeSettings?.requiresShipping !== false && (
          <div className="os-subtotal">
            <span>{t.shipping}</span>
            {isSummaryLoading || showLoadingOverlay ? (
              <span className="os-loading-placeholder">...</span>
            ) : (
              <span>{getShippingDisplayText()}</span>
            )}
          </div>
        )}

        {/* Free shipping notification - only show when not loading and shipping is required */}
        {storeSettings?.requiresShipping !== false &&
          !isSummaryLoading &&
          !showLoadingOverlay &&
          getFreeShippingNotification() && (
            <div
              className={`os-free-shipping-notification ${
                getFreeShippingNotification().type
              }`}
            >
              <span>{getFreeShippingNotification().message}</span>
            </div>
          )}

        {storeSettings?.taxRate > 0 && (
          <div className="os-subtotal">
            <span>{getTaxDisplayText()}</span>
            {renderValueOrLoading(taxAmount)}
          </div>
        )}

        <div className="os-total">
          <span>{t.total}</span>
          {renderValueOrLoading(finalTotal)}
        </div>
      </div>

      <button
        type="submit"
        className="os-checkout-btn"
        disabled={isCheckoutDisabled()}
      >
        {getCheckoutButtonText()}
      </button>

      <button
        type="button"
        className="os-back-to-cart"
        onClick={() => navigate(-1)}
        disabled={processing || isValidating || isCalculating || promoLoading}
      >
        {t.backToCart}
      </button>
    </div>
  );
};

export default OrderSummary;
